import React, { useState } from "react";
import { FiAlertTriangle, FiAlertOctagon, FiCheckCircle, FiFilter, FiBell } from "react-icons/fi";
import { deviceDummyData } from "../../../Data/DeviceData";

const SEV_META = {
  Critical: { color: "#ef4444", bg: "#fee2e2", icon: FiAlertOctagon },
  Warning:  { color: "#f59e0b", bg: "#fef3c7", icon: FiAlertTriangle },
  Info:     { color: "#3b82f6", bg: "#dbeafe", icon: FiBell },
};

const DvcAlertsPanel = () => {
  const [filter, setFilter]  = useState("All");
  const [showAck, setShowAck] = useState(false);
  const [alerts, setAlerts]  = useState(deviceDummyData.alerts);
  const [selected, setSelected] = useState(null);

  const filtered = alerts.filter(a => {
    if (!showAck && a.ack) return false;
    if (filter !== "All" && a.severity !== filter) return false;
    return true;
  });

  const ackAlert = (id) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, ack: true } : a));
    if (selected?.id === id) setSelected(prev => ({ ...prev, ack: true }));
  };

  const critCount = alerts.filter(a => a.severity === "Critical" && !a.ack).length;
  const warnCount = alerts.filter(a => a.severity === "Warning"  && !a.ack).length;

  return (
    <div className="dvc-alerts-panel">
      {/* Summary chips */}
      <div className="dvc-alert-summary">
        <div className="dvc-alert-chip" style={{ background: "#fee2e2", color: "#ef4444", borderColor: "#fca5a5" }}>
          <FiAlertOctagon size={16} /> {critCount} Critical
        </div>
        <div className="dvc-alert-chip" style={{ background: "#fef3c7", color: "#f59e0b", borderColor: "#fde68a" }}>
          <FiAlertTriangle size={16} /> {warnCount} Warning
        </div>
        <div className="dvc-alert-chip" style={{ background: "#d1fae5", color: "#10b981", borderColor: "#6ee7b7" }}>
          <FiCheckCircle size={16} /> {alerts.filter(a => a.ack).length} Acknowledged
        </div>
      </div>

      {/* Filters */}
      <div className="dvc-filter-bar" style={{ marginTop: 0 }}>
        <div className="dvc-filter-group">
          <FiFilter size={13} color="#6b7280" />
          {["All","Critical","Warning"].map(f => (
            <button key={f} className={`dvc-view-btn ${filter === f ? "dvc-view-active" : ""}`} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
          <input type="checkbox" checked={showAck} onChange={e => setShowAck(e.target.checked)} />
          Show Acknowledged
        </label>
      </div>

      <div className="dvc-alerts-body">
        {/* Alert List */}
        <div className="dvc-alert-list">
          {filtered.map(a => {
            const m = SEV_META[a.severity] || SEV_META.Info;
            const isSelected = selected?.id === a.id;
            return (
              <div key={a.id}
                className={`dvc-alert-row ${isSelected ? "dvc-alert-selected" : ""} ${a.ack ? "dvc-alert-acked" : ""}`}
                style={{ borderLeftColor: m.color }}
                onClick={() => setSelected(isSelected ? null : a)}
              >
                <m.icon size={16} color={a.ack ? "#9ca3af" : m.color} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: a.ack ? "#9ca3af" : "#1e293b" }}>{a.deviceName}</span>
                    <span style={{ padding: "1px 7px", borderRadius: 10, background: a.ack ? "#f3f4f6" : m.bg, color: a.ack ? "#9ca3af" : m.color, fontSize: 11, fontWeight: 700 }}>{a.severity}</span>
                    <span style={{ padding: "1px 7px", borderRadius: 10, background: "#f3f4f6", color: "#6b7280", fontSize: 11 }}>{a.type}</span>
                    {a.ack && <span style={{ fontSize: 11, color: "#10b981", fontWeight: 600 }}>✓ Acked</span>}
                  </div>
                  <div style={{ fontSize: 12, color: a.ack ? "#9ca3af" : "#374151", marginTop: 4 }}>{a.msg}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{a.time}</div>
                </div>
                {!a.ack && (
                  <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ fontSize: 11, padding: "4px 10px", flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); ackAlert(a.id); }}>
                    Acknowledge
                  </button>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="dvc-empty" style={{ padding: 40 }}>
              <FiCheckCircle size={32} color="#10b981" style={{ marginBottom: 8 }} />
              <div>No alerts match current filters</div>
            </div>
          )}
        </div>

        {/* Detail */}
        {selected && (() => {
          const m = SEV_META[selected.severity] || SEV_META.Info;
          return (
            <div className="dvc-alert-detail" style={{ borderTopColor: m.color }}>
              <div className="dvc-section-title">Alert Detail</div>
              <div style={{ marginTop: 10 }}>
                {[
                  { label: "Alert ID",   val: selected.id },
                  { label: "Device",     val: `${selected.deviceName} (${selected.deviceId})` },
                  { label: "Severity",   val: selected.severity },
                  { label: "Type",       val: selected.type },
                  { label: "Time",       val: selected.time },
                  { label: "Status",     val: selected.ack ? "Acknowledged" : "Active" },
                ].map(r => (
                  <div key={r.label} className="dvc-info-row">
                    <div className="dvc-info-lbl">{r.label}</div>
                    <div className="dvc-info-val">{r.val}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, padding: 10, background: m.bg, borderRadius: 8, fontSize: 12, color: m.color, fontWeight: 600 }}>
                {selected.msg}
              </div>
              {!selected.ack && (
                <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ marginTop: 12, width: "100%" }}
                  onClick={() => ackAlert(selected.id)}>
                  ✓ Acknowledge Alert
                </button>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default DvcAlertsPanel;
